import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Plus, MagnifyingGlass, Star, Clock, Fire, ChartBar, Medal 
} from '@phosphor-icons/react';
import { getToken } from '@/utils/auth';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BROWSE_MODES = {
  'top-rated': '-rating',
  'newest': '-released',
  'trending': '-added',
  'most-reviewed': '-reviews_count',
  'metacritic': '-metacritic'
};

export const RawgTab = ({ onGamesUpdated }) => {
  const [rawgGames, setRawgGames] = useState([]);
  const [rawgQuery, setRawgQuery] = useState('');
  const [rawgLoading, setRawgLoading] = useState(false);

  const searchRawgGames = async (orderBy = '-rating') => {
    setRawgLoading(true);
    try {
      const params = new URLSearchParams();
      if (rawgQuery) params.set('query', rawgQuery);
      params.set('ordering', orderBy);
      params.set('page_size', '20');
      
      const response = await axios.get(`${API}/external/rawg/search?${params.toString()}`);
      setRawgGames(response.data.results);
      const cacheStatus = response.data.from_cache ? '⚡ from cache' : '🌐 fresh from RAWG';
      toast.success(`Found ${response.data.count.toLocaleString()} games ${cacheStatus}`);
    } catch (error) {
      toast.error('Failed to search RAWG games');
    } finally {
      setRawgLoading(false);
    }
  };

  const browseRawg = async (mode) => {
    setRawgQuery('');
    await searchRawgGames(BROWSE_MODES[mode] || '-rating');
  };

  const importRawgGame = async (externalId) => {
    try {
      const token = getToken();
      await axios.post(`${API}/external/rawg/import`, 
        { external_id: externalId, source: 'rawg' },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Game imported from RAWG!');
      onGamesUpdated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import game');
    }
  };

  const browseButtons = [
    { mode: 'top-rated', label: 'Top Rated', icon: <Star size={14} weight="fill" className="mr-1.5 text-[#F59E0B]" />, testid: 'browse-top-rated' },
    { mode: 'newest', label: 'Newest', icon: <Clock size={14} weight="fill" className="mr-1.5 text-[#10B981]" />, testid: 'browse-newest' },
    { mode: 'trending', label: 'Trending Now', icon: <Fire size={14} weight="fill" className="mr-1.5 text-[#EF4444]" />, testid: 'browse-trending' },
    { mode: 'most-reviewed', label: 'Most Reviewed', icon: <ChartBar size={14} weight="fill" className="mr-1.5 text-[#8B5CF6]" />, testid: 'browse-most-reviewed' },
    { mode: 'metacritic', label: 'Best Metacritic', icon: <Medal size={14} weight="fill" className="mr-1.5 text-[#0047FF]" />, testid: 'browse-metacritic' },
  ];

  return (
    <div className="p-6 pt-4">
      <div className="flex gap-2 mb-3">
        <Input
          value={rawgQuery}
          onChange={(e) => setRawgQuery(e.target.value)}
          placeholder="Search 500,000+ games (e.g., 'witcher', 'minecraft')..."
          className="flex-1 border-[#E4E4E7]"
          onKeyPress={(e) => e.key === 'Enter' && searchRawgGames()}
          data-testid="rawg-search-input"
        />
        <Button onClick={() => searchRawgGames()} disabled={rawgLoading} className="bg-[#0047FF]" data-testid="rawg-search-button">
          <MagnifyingGlass size={18} className="mr-2" />
          {rawgLoading ? 'Searching...' : 'Search RAWG'}
        </Button>
      </div>

      <div className="mb-4">
        <p className="text-xs font-medium text-[#71717A] uppercase tracking-wider mb-2">Quick Browse</p>
        <div className="flex flex-wrap gap-2">
          {browseButtons.map((btn) => (
            <Button
              key={btn.mode}
              size="sm"
              variant="outline"
              onClick={() => browseRawg(btn.mode)}
              disabled={rawgLoading}
              className="border-[#E4E4E7] hover:border-[#0047FF] hover:bg-[#0047FF]/5"
              data-testid={btn.testid}
            >
              {btn.icon}
              {btn.label}
            </Button>
          ))}
        </div>
      </div>
      <p className="text-xs text-[#71717A] mb-4">
        Powered by RAWG.io - 500K+ games · ⚡ Smart 24h caching reduces API calls by 60-70%
      </p>

      <ScrollArea className="h-[400px]">
        {rawgGames.length === 0 && !rawgLoading && (
          <div className="text-center py-12">
            <MagnifyingGlass size={48} className="mx-auto text-[#E4E4E7] mb-3" />
            <p className="text-sm text-[#71717A]">Search RAWG.io or click a quick browse mode above</p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          {rawgGames.map((game) => (
            <div key={game.external_id} className="p-3 border border-[#E4E4E7] hover:bg-[#FAFAFA]">
              <img src={game.image} alt={game.name} className="w-full h-32 object-cover mb-2" />
              <p className="font-medium text-sm text-[#09090B] truncate">{game.name}</p>
              <p className="text-xs text-[#71717A]">{game.genre}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs font-mono text-[#0047FF]">★ {game.rating?.toFixed(1) || 'N/A'}</span>
                {game.metacritic && (
                  <span className="text-xs font-mono text-[#10B981]">MC: {game.metacritic}</span>
                )}
              </div>
              <p className="text-xs text-[#71717A] mt-1 truncate">{game.platform}</p>
              <Button size="sm" onClick={() => importRawgGame(game.external_id)} className="w-full bg-[#0047FF] mt-2">
                <Plus size={16} className="mr-1" /> Import
              </Button>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};
